import {
  RuleCondition,
  RuleExpr,
  RuleMemory,
  RuleDefinition,
  ConditionDef,
} from "../interfaces/IconDefinitions";
import { SocketMessageInterface } from "../interfaces/Monitoring.interface";
import { DeviceDirection } from "../types/DeviceDirection";
import { bitmaskPrefix } from "./typedKeys";
import { msgTypeLabel } from "../flatbuffers/decoders/applyDeltaToState";
import { EncodedMessageContent } from "../flatbuffers/generated/ln2/encoded-message-content";
import { MessageContent } from "../flatbuffers/generated/ln2/message-content";
import { Usage } from "../flatbuffers/generated/usage/usage";
import { Service } from "../flatbuffers/generated/service/service";
import { Traffic } from "../flatbuffers/generated/traffic/traffic";
import { Control } from "../flatbuffers/generated/control/control";
import { Fault } from "../flatbuffers/generated/fault/fault";
import { Status } from "../flatbuffers/generated/status/status";
import { GroupSupervisoryStatus } from "../flatbuffers/generated/group-supervisory-status/group-supervisory-status";
import { GroupControl } from "../flatbuffers/generated/group-control/group-control";

/**
 * Case-insensitive, whitespace-trimmed string equality. Treats null and undefined as empty.
 */
export function eqCI(a?: string | null, b?: string | null): boolean {
  return (a ?? "").trim().toUpperCase() === (b ?? "").trim().toUpperCase();
}

/**
 * Whether a device direction satisfies a rule's direction condition.
 *
 * A null rule value means "any direction", so it always matches.
 */
export function directionMatchesRule(dir: DeviceDirection, ruleValue: DeviceDirection): boolean {
  if (ruleValue === null) return true;
  return dir === ruleValue;
}

/**
 * Applies a predicate only when the rule specifies a value. A null rule value matches anything.
 */
export function matchIfNotNull<T>(ruleValue: T | null, predicate: (v: T) => boolean): boolean {
  if (ruleValue === null) return true;
  return predicate(ruleValue);
}

/**
 * Evaluates a rule expression against the memory of already-resolved conditions.
 */
export function evalExpr(expr: RuleExpr, memory: RuleMemory): boolean {
  if (expr.kind === "ref") {
    return !!memory[expr.id];
  }

  if (expr.children.length === 0) return false;

  if (expr.op === "AND") {
    return expr.children.every((child) => evalExpr(child, memory));
  }

  return expr.children.some((child) => evalExpr(child, memory));
}

/**
 * Resolve a condition's message number to the string label used in bitmask keys.
 */
function resolveConditionValueLabel(
  contentType: EncodedMessageContent,
  messageType: MessageContent | MessageContent,
  message: number,
  escalator?: boolean,
): string {
  if (contentType === EncodedMessageContent.OneHotMessage) {
    switch (messageType as MessageContent) {
      case MessageContent.Usage_UsageMessage:
        return (escalator ? Usage : Usage)[message] ?? "";
      case MessageContent.Service_ServiceMessage:
        return Service[message] ?? "";
      default:
        return "";
    }
  }
  if (contentType === EncodedMessageContent.BitMaskMessage) {
    switch (messageType as MessageContent) {
      case MessageContent.Traffic_TrafficMessage:
        return Traffic[message] ?? "";
      case MessageContent.Control_ControlMessage:
        return Control[message] ?? "";
      case MessageContent.Fault_FaultMessage:
        return Fault[message] ?? "";
      case MessageContent.Status_StatusMessage:
        return Status[message] ?? "";
      case MessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage:
        return GroupSupervisoryStatus[message] ?? "";
      case MessageContent.GroupControl_GroupControlMessage:
        return GroupControl[message] ?? "";
      case MessageContent.ControllerInfo_ControllerInfoMessage:
        return "";
      default:
        return "";
    }
  }
  return "";
}

/**
 * Build the bitmask key suffix ("Type:Value") from a condition's numeric fields.
 * Returns null when the condition lacks the info needed for a bitmask lookup.
 */
function conditionBitmaskSuffix(condition: RuleCondition, escalator?: boolean): string | null {
  if (
    condition.contentType == null ||
    condition.contentType === EncodedMessageContent.NONE ||
    condition.message == null
  ) {
    return null;
  }
  const typeLabel = msgTypeLabel(condition.contentType, condition.messageType);
  if (!typeLabel) return null;
  const valueLabel = resolveConditionValueLabel(
    condition.contentType,
    condition.messageType,
    condition.message,
    escalator,
  );
  if (!valueLabel) return null;
  return `${typeLabel}:${valueLabel}`;
}

/**
 * The first rule whose expression evaluates true, in definition order.
 *
 * Order is significant: a broader rule placed above a narrower one shadows it.
 */
export function findWinningRule(
  ruleDefs: RuleDefinition[],
  memory: RuleMemory,
): RuleDefinition | null {
  return ruleDefs.find((rule) => evalExpr(rule.activeWhen, memory)) ?? null;
}

/**
 * Whether one condition holds for the current elevator socket state.
 */
export function conditionMatchesSocket(
  condition: RuleCondition,
  socketMsg: SocketMessageInterface,
): boolean {
  const suffix = conditionBitmaskSuffix(condition);
  if (suffix) {
    const prefix = bitmaskPrefix(socketMsg.bankId, socketMsg.liftId);
    const mapActive = socketMsg.bitmask[`${prefix}${suffix}`] ?? false;
    return (
      matchIfNotNull(condition.active, (v) => mapActive === v) &&
      matchIfNotNull(condition.frontDoor, (v) => socketMsg.doorStatus.frontOpen === v) &&
      matchIfNotNull(condition.rearDoor, (v) => socketMsg.doorStatus.rearOpen === v) &&
      directionMatchesRule(socketMsg.direction, condition.direction)
    );
  }

  // Fallback: numeric comparison against the latest message
  return (
    (condition.contentType == null || socketMsg.msg.contentType === condition.contentType) &&
    (condition.messageType === 0 || socketMsg.msg.messageType === condition.messageType) &&
    matchIfNotNull(condition.active, (v) => socketMsg.msg.active === v) &&
    matchIfNotNull(condition.frontDoor, (v) => socketMsg.doorStatus.frontOpen === v) &&
    matchIfNotNull(condition.rearDoor, (v) => socketMsg.doorStatus.rearOpen === v) &&
    directionMatchesRule(socketMsg.direction, condition.direction)
  );
}

/**
 * Whether one condition holds for the current escalator socket state.
 *
 * Escalators report a different state set from elevators, which is why this is separate.
 */
export function conditionMatchesSocketEscalator(
  condition: RuleCondition,
  socketMsg: SocketMessageInterface,
): boolean {
  const suffix = conditionBitmaskSuffix(condition, true);
  if (suffix) {
    const prefix = bitmaskPrefix(socketMsg.bankId, socketMsg.liftId);
    const mapActive = socketMsg.bitmask[`${prefix}${suffix}`] ?? false;
    return (
      matchIfNotNull(condition.active, (v) => mapActive === v) &&
      directionMatchesRule(socketMsg.direction, condition.direction)
    );
  }

  // Fallback: numeric comparison against the latest message
  return (
    (condition.contentType == null || socketMsg.msg.contentType === condition.contentType) &&
    (condition.messageType === 0 || socketMsg.msg.messageType === condition.messageType) &&
    matchIfNotNull(condition.active, (v) => socketMsg.msg.active === v) &&
    directionMatchesRule(socketMsg.direction, condition.direction)
  );
}

/**
 * Pre-evaluates every condition referenced by the rule set, so expressions can be
 * resolved by lookup instead of re-checking the socket for each reference.
 */
export function buildRuleMemory(
  rules: RuleDefinition[],
  socketMsg: SocketMessageInterface,
  escalator?: boolean,
): RuleMemory {
  const matcher = escalator ? conditionMatchesSocketEscalator : conditionMatchesSocket;
  const memory: RuleMemory = {};
  rules.forEach((r) => {
    r.conditions.forEach((def) => {
      if (matcher(def.condition, socketMsg)) {
        memory[def.id] = true;
      }
    });
  });
  return memory;
}

/**
 * Incrementally accumulate condition matches into existing memory.
 * Only adds new truths — once a condition is true it stays true until
 * the caller resets memory (e.g. on rule transition).
 * Returns the same reference if nothing changed.
 */
export function updateMemoryWithMessage(
  prev: RuleMemory,
  rules: RuleDefinition[],
  socketMsg: SocketMessageInterface,
  matcher: (c: RuleCondition, s: SocketMessageInterface) => boolean,
): RuleMemory {
  let changed = false;
  const next: RuleMemory = { ...prev };

  rules.forEach((rule) => {
    if (rule && rule.conditions) {
      rule.conditions.forEach((def) => {
        if (!next[def.id] && matcher(def.condition, socketMsg)) {
          next[def.id] = true;
          changed = true;
        }
      });
    }
  });

  return changed ? next : prev;
}

/**
 * Evaluate an expression using only the conditions that match the current
 * socket message — no historical memory. Useful for checking removeWhen
 * and for finding replacer rules where we want instant evaluation scoped
 * to a single rule's conditions.
 */
export function matchesExprOnMessage(
  expr: RuleExpr,
  conditions: ConditionDef[],
  socketMsg: SocketMessageInterface,
  matcher: (c: RuleCondition, s: SocketMessageInterface) => boolean,
): boolean {
  const instant: RuleMemory = {};
  conditions.forEach((def) => {
    if (matcher(def.condition, socketMsg)) {
      instant[def.id] = true;
    }
  });
  return evalExpr(expr, instant);
}
